import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// The single real access-enforcement point for CostSpace, per the
// explicit requirement: "The database must remain the source of truth
// ... do not duplicate the calculation in React." This component calls
// has_costspace_access(organization_id) -- the one authoritative
// server-side function -- and renders nothing but a loading state or a
// blocked screen; it never computes trial/expiry logic itself.
//
// Platform admins bypass this gate entirely (checked first, via the
// same is_platform_admin() used throughout Subscription Management) --
// an admin must always be able to reach the app (and Subscription
// Management specifically) even if their own organization's
// subscription has lapsed, otherwise no one could ever fix it.
export default function SubscriptionGate({ organizationId, children }) {
  const [state, setState] = useState('loading') // 'loading' | 'allowed' | 'blocked'
  const [status, setStatus] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function check() {
      const { data: isAdmin } = await supabase.rpc('is_platform_admin')
      if (cancelled) return
      if (isAdmin) { setState('allowed'); return }

      if (!organizationId) {
        // No organization on the profile at all -- cannot have a valid
        // subscription. Blocked, not a silent pass-through.
        setState('blocked')
        setStatus(null)
        return
      }

      const [{ data: hasAccess }, { data: currentStatus }] = await Promise.all([
        supabase.rpc('has_costspace_access', { p_organization_id: organizationId }),
        supabase.rpc('get_organization_subscription_status', { p_organization_id: organizationId }),
      ])
      if (cancelled) return
      setStatus(currentStatus)
      setState(hasAccess ? 'allowed' : 'blocked')
    }

    check()
    return () => { cancelled = true }
  }, [organizationId])

  if (state === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: '#1C1A18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9A948C', fontSize: 13 }}>
        Loading…
      </div>
    )
  }

  if (state === 'blocked') {
    return <SubscriptionBlockedScreen status={status} />
  }

  return children
}

function SubscriptionBlockedScreen({ status }) {
  const messages = {
    expired: { title: 'Your trial has ended', body: 'Your CostSpace trial period has expired. Contact us to continue with a paid subscription.' },
    suspended: { title: 'Account suspended', body: 'Your organization\'s CostSpace access has been suspended. Contact your administrator for details.' },
    cancelled: { title: 'Subscription cancelled', body: 'Your organization\'s CostSpace subscription has been cancelled.' },
  }
  const msg = messages[status] || { title: 'Access unavailable', body: 'Your organization does not currently have an active CostSpace subscription. Contact us for assistance.' }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F1EA', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 420, textAlign: 'center', background: '#fff', border: '1px solid #DDD9D0', borderRadius: 16, padding: 40 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1C1A18', marginBottom: 10 }}>{msg.title}</h1>
        <p style={{ fontSize: 14, color: '#6B655C', lineHeight: 1.6, marginBottom: 24 }}>{msg.body}</p>
        <a href="mailto:wessam.taha.ahmed@gmail.com" style={{ display: 'inline-block', background: '#0B3D91', color: '#fff', padding: '11px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
          Contact Us
        </a>
      </div>
    </div>
  )
}
